/**
 * FarmersPostingStrip — small "3 farmers posting this now" strip rendered
 * on FoodPulse produce guides + food-item pages. Pulls active crop-posts
 * from the FarmersPulse cross-site bridge (`/api/farmers-pulse/produce/
 * {slug}/farmers`) and renders a horizontally-scrollable set of cards.
 *
 * Renders nothing when there are no active posts so it never leaves a
 * lonely empty state on ingredients no farmer is currently listing.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tractor, MapPin, Clock, ExternalLink } from "lucide-react";
import { http } from "@/lib/api";

function readCity() {
  try {
    const raw = localStorage.getItem("fp_user_location") || localStorage.getItem("fp:userLocation");
    const j = raw ? JSON.parse(raw) : null;
    return j?.city || null;
  } catch { return null; }
}

function prettyAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const mins = Math.floor((Date.now() - t) / 60000);
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / (60 * 24))}d ago`;
  } catch { return ""; }
}

export default function FarmersPostingStrip({ slug, className = "" }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | empty | ok | error
  const [scope, setScope] = useState("all"); // all | local

  useEffect(() => {
    if (!slug) return;
    const city = readCity();
    setStatus("loading");
    (async () => {
      try {
        const params = city ? { city } : {};
        const r = await http.get(`/farmers-pulse/produce/${slug}/farmers`, { params });
        const localItems = r.data?.items || [];
        if (city && localItems.length > 0) {
          setItems(localItems);
          setTotal(r.data?.total || localItems.length);
          setScope("local");
          setStatus("ok");
          return;
        }
        // Fallback — no matches in the caller's city → show global.
        const rAll = await http.get(`/farmers-pulse/produce/${slug}/farmers`);
        const allItems = rAll.data?.items || [];
        setItems(allItems);
        setTotal(rAll.data?.total || allItems.length);
        setScope("all");
        setStatus(allItems.length ? "ok" : "empty");
      } catch {
        setStatus("error");
      }
    })();
  }, [slug]);

  if (status === "empty" || status === "error") return null;

  return (
    <div className={`rounded-2xl border border-amber-200 bg-amber-50/60 p-4 ${className}`} data-testid="farmers-posting-strip">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-content-center rounded-full bg-white">
            <Tractor className="h-4 w-4 text-amber-700" />
          </span>
          <div>
            <div className="text-sm font-semibold text-amber-900" data-testid="farmers-posting-title">
              {total} {total === 1 ? "farmer is" : "farmers are"} posting this now
            </div>
            <div className="text-[11px] text-amber-800/80">
              {scope === "local" ? "In your area · " : "Across the country · "}
              Fresh from FarmersPulse.com
            </div>
          </div>
        </div>
        <a
          href="https://farmerspulse.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="farmers-posting-visit"
          className="hidden shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 sm:inline-flex"
        >
          Visit FarmersPulse <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {status === "loading" ? (
        <div className="mt-3 text-xs text-amber-800/70">Loading fresh farm posts…</div>
      ) : (
        <div className="mt-3 flex snap-x gap-3 overflow-x-auto pb-1" data-testid="farmers-posting-list">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`farmers-posting-item-${it.id}`}
              className="min-w-[220px] max-w-[240px] shrink-0 snap-start rounded-xl border border-amber-100 bg-white p-3"
            >
              <div className="flex items-center gap-2">
                <div className="grid h-7 w-7 place-content-center rounded-full bg-amber-100 text-[10px] font-semibold text-amber-800">
                  {(it.farm_slug || "F").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">
                    {(it.farm_slug || "farm").replace(/-/g, " ")}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-slate-500">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate">{it.city || it.state || "USA"}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-700">
                {it.qty ? `${it.qty} ${it.unit || "unit"}` : "Fresh crop"}
                {typeof it.price === "number" && (
                  <span className="ml-1 text-emerald-700">· ${it.price.toFixed(2)}{it.unit ? `/${it.unit}` : ""}</span>
                )}
              </div>
              {it.notes && <div className="mt-1 line-clamp-2 text-[11px] text-slate-500">{it.notes}</div>}
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1 text-slate-400"><Clock className="h-3 w-3" />{prettyAgo(it.created_at)}</span>
                {it.deal_id ? (
                  <Link
                    to={`/food-pulse/deals`}
                    data-testid={`farmers-posting-cta-${it.id}`}
                    className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-white hover:bg-emerald-700"
                  >
                    View deal
                  </Link>
                ) : (
                  <span className="text-amber-700">Pending review</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
